import { GitBranch, Loader2, ScanSearch } from 'lucide-react'

import { DataTable, type Column } from '@/components/data/DataTable'
import { DetailPane, DetailSection } from '@/components/data/DetailPane'
import { LoadingRows } from '@/components/data/SearchInput'
import { SplitView } from '@/components/data/SplitView'
import { StatusText } from '@/components/data/StatusText'
import type {
  CustomizationCategory,
  CustomizationItem,
  CustomizationsReport,
  WorkflowMeta,
} from '@/lib/customizations'
import { useSelection } from '@/lib/useSelection'

import { SectionStrip } from './sections/SectionLabel'

/** state of the on-demand check, owned by ProjectDetail so the tile can show the count */
export type CustomizationsState =
  | { status: 'idle' }
  | { status: 'loading' }
  | { status: 'error'; error: string }
  | { status: 'done'; report: CustomizationsReport }

const s = (v: unknown): string => (v == null ? '' : String(v))

// Readable label for an app-field `type` string; full value stays in the title tooltip.
//   ari:cloud:ecosystem::extension/<uuid>/<uuid>/static/<name>  -> "<name>"
//   connect:<key>__<module> / forge:<...>                       -> last meaningful segment
//   anything else                                               -> unchanged
function appFieldLabel(type: string): string {
  if (!type) return ''
  const staticIdx = type.lastIndexOf('/static/')
  if (staticIdx !== -1) return type.slice(staticIdx + '/static/'.length)
  if (type.startsWith('connect:') || type.startsWith('forge:')) {
    const tail = type.split(/[:/]/).filter(Boolean).pop()
    return tail ?? type
  }
  return type
}

const cfgOf = (it: CustomizationItem): Record<string, string> =>
  it.config && typeof it.config === 'object' ? (it.config as Record<string, string>) : {}

// A rule's configuration (key: value) — "what's inside" a workflow function.
function ConfigLines({ config }: { config: Record<string, string> }) {
  const entries = Object.entries(config)
  if (entries.length === 0) return null
  return (
    <div className="mt-1 space-y-0.5 border-l border-line-strong pl-2">
      {entries.map(([k, v]) => (
        <div key={k} className="font-mono text-[11px] text-text-2">
          <span className="text-text-3">{k}:</span>{' '}
          <span className="whitespace-pre-wrap break-words">{v}</span>
        </div>
      ))}
    </div>
  )
}

/** One item of a category — a field (name + type) or a workflow rule. */
function ItemRow({ item }: { item: CustomizationItem }) {
  if (item.rule_key !== undefined) {
    return (
      <li className="py-1.5">
        <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 font-mono text-[12px]">
          <span className="text-foreground">{s(item.workflow)}</span>
          <span aria-hidden className="text-text-3">▸</span>
          <span className="text-text-2">{s(item.transition) || '—'}</span>
          <span className="rounded border border-line-strong px-1.5 text-[11px] text-text-2">{s(item.rule_kind)}</span>
        </div>
        <div className="mt-0.5 break-all font-mono text-[11px] text-text-3">{s(item.rule_key)}</div>
        <ConfigLines config={cfgOf(item)} />
      </li>
    )
  }
  return (
    <li className="py-1.5">
      <div className="text-[12.5px] text-foreground">{s(item.name) || s(item.id)}</div>
      <div className="mt-0.5 break-all font-mono text-[11px] text-text-3" title={s(item.type)}>
        {appFieldLabel(s(item.type))}
      </div>
    </li>
  )
}

// Workflow rules grouped by workflow → transition → rules, so the flow reads:
// "this workflow handles these issue types; these transitions go from→to, and these rules fire".
function WorkflowGroups({ items, workflows }: { items: CustomizationItem[]; workflows?: WorkflowMeta[] }) {
  const issueTypesOf = (wf: string) => workflows?.find((w) => w.name === wf)?.issue_types ?? []
  const byWorkflow = new Map<string, Map<string, CustomizationItem[]>>()
  for (const it of items) {
    const wf = s(it.workflow) || '—'
    const tr = s(it.transition) || '—'
    if (!byWorkflow.has(wf)) byWorkflow.set(wf, new Map())
    const byTransition = byWorkflow.get(wf)!
    if (!byTransition.has(tr)) byTransition.set(tr, [])
    byTransition.get(tr)!.push(it)
  }
  return (
    <div className="space-y-3">
      {[...byWorkflow.entries()].map(([wf, transitions]) => (
        <section key={wf} className="border-t border-line pt-2 first:border-0 first:pt-0">
          <div className="flex flex-wrap items-center gap-1.5">
            <GitBranch aria-hidden className="size-3.5 text-text-3" />
            <span className="font-mono text-[12px] font-medium text-foreground">{wf}</span>
            <span className="font-mono text-[11px] text-text-3">
              {[...transitions.values()].reduce((n, r) => n + r.length, 0)}
            </span>
            {issueTypesOf(wf).map((t) => (
              <span key={t} className="rounded border border-line-strong px-1.5 text-[11px] text-text-2">{t}</span>
            ))}
          </div>
          <div className="mt-1.5 space-y-2">
            {[...transitions.entries()].map(([tr, rules]) => {
              const from = s(rules[0]?.from_status)
              const to = s(rules[0]?.to_status)
              return (
                <div key={tr} className="border-l border-line-strong pl-2.5">
                  <div className="flex flex-wrap items-center gap-x-1.5 font-mono text-[12px]">
                    <span className="font-medium text-foreground">{tr}</span>
                    {(from || to) && <span className="text-text-2">{from || '—'} → {to || '—'}</span>}
                  </div>
                  <div className="mt-0.5 space-y-1">
                    {rules.map((r, i) => (
                      <div key={i}>
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="rounded border border-line-strong px-1.5 text-[11px] text-text-2">{s(r.rule_kind)}</span>
                          <span className="break-all font-mono text-[11px] text-text-3">{s(r.rule_key)}</span>
                        </div>
                        <ConfigLines config={cfgOf(r)} />
                      </div>
                    ))}
                  </div>
                </div>
              )
            })}
          </div>
        </section>
      ))}
    </div>
  )
}

const catStatus = (c: CustomizationCategory) =>
  c.status === 'ok' ? (c.items.length ? 'found' : 'none found') : c.status === 'error' ? 'error' : 'not checkable via API'

const COLUMNS: Column<CustomizationCategory>[] = [
  { key: 'label', header: 'Category' },
  { key: 'found', header: 'Found', mono: true, width: '96px', align: 'right', render: (c) => (c.status === 'ok' ? c.items.length : '—') },
  {
    key: 'status', header: 'Status', width: '24%', title: catStatus,
    render: (c) => (c.status === 'error'
      ? <StatusText status="error" />
      : <span className={c.status === 'ok' && c.items.length ? 'text-warn' : 'text-text-2'}>{catStatus(c)}</span>),
  },
]

/** Non-standard logic in a project (scripted/app fields, workflow scripts, ScriptRunner) — on demand. */
export default function ProjectCustomizations({ state, onRun }: { state: CustomizationsState; onRun: () => void }) {
  const [sel, setSel] = useSelection()
  const report = state.status === 'done' ? state.report : null
  const selected = report?.categories.find((c) => c.key === sel) ?? null
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <SectionStrip label="customizations">
        <button
          type="button"
          disabled={state.status === 'loading'}
          onClick={onRun}
          className="inline-flex h-7 items-center gap-1.5 rounded-md bg-primary px-2.5 text-[12px] font-medium text-accent-fg transition-colors hover:bg-primary/90 disabled:opacity-60"
        >
          {state.status === 'loading'
            ? <Loader2 aria-hidden className="size-3.5 animate-spin" />
            : <ScanSearch aria-hidden className="size-3.5" />}
          {state.status === 'loading' ? 'Scanning…' : report ? 'Run check again' : 'Run check'}
        </button>
        <span className="text-[12px] text-text-2">
          Scripted fields, app fields, workflow scripts and ScriptRunner items in this project.
        </span>
        {report && report.warnings.length > 0 && (
          <span className="text-[12px] text-warn">{report.warnings.join(' · ')}</span>
        )}
      </SectionStrip>
      {state.status === 'idle' && <p className="px-4 py-4 text-[13px] text-text-2">Run the check to scan this project.</p>}
      {state.status === 'loading' && <LoadingRows label="Scanning the project" rows={4} />}
      {state.status === 'error' && <p role="alert" className="px-4 py-4 font-mono text-[12px] text-danger">{state.error}</p>}
      {report && (
        <SplitView
          onCloseDetail={() => setSel(null)}
          detail={selected && (
            <DetailPane
              eyebrow="Customizations"
              title={selected.label}
              status={<span className="text-text-2">{catStatus(selected)}</span>}
            >
              {selected.note && <p className="text-[12px] text-text-2">{selected.note}</p>}
              {selected.status === 'ok' && selected.items.length > 0 && (
                <DetailSection title="Items">
                  {selected.key === 'workflow_scripts'
                    ? <WorkflowGroups items={selected.items} workflows={report.workflows} />
                    : (
                      <ul className="divide-y divide-line-row">
                        {selected.items.map((it, i) => <ItemRow key={i} item={it} />)}
                      </ul>
                    )}
                </DetailSection>
              )}
            </DetailPane>
          )}
          list={(
            <DataTable label="Customization categories" rows={report.categories} columns={COLUMNS}
              rowId={(c) => c.key} selectedId={sel} onSelect={setSel} empty="No categories reported." />
          )}
        />
      )}
    </div>
  )
}
